import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import { fetchAllPaginated } from '../lib/fetchAllPaginated';

export interface SalesHistoryRecord {
  id: string;
  admin_id: string;
  product_id: string;
  product_name: string;
  sku: string | null;
  reference_code: string | null;
  color_name: string | null;
  size_name: string | null;
  quantity_sold: number;
  unit_price: number;
  sold_at: string;
  week_start_date: string;
  week_end_date: string;
  reservation_status: 'pending' | 'reserved' | 'not_available' | 'collected';
  reservation_id: string | null;
  shopify_order_number: string | null;
  shopify_order_id: string | null;
  reservation_email_sent: boolean;
  created_at: string;
  updated_at: string;
}

export interface WeekStats {
  total_sold: number;
  pending: number;
  reserved: number;
  not_available: number;
  collected: number;
}

export interface CollectionSettings {
  id?: string;
  admin_id: string;
  is_enabled: boolean;
  collection_days: string[];
  collection_time: string;
  last_processed_at: string | null;
}

export interface NotificationEmail {
  id: string;
  admin_id: string;
  email: string;
  label: string | null;
  is_active: boolean;
  report_format: 'excel' | 'pdf';
  created_at: string;
}

interface SalesHistoryFilters {
  weekStart?: string;
  weekEnd?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ConsolidatedSale {
  key: string;
  product_name: string;
  color_name: string | null;
  size_name: string | null;
  reference_code: string | null;
  sku: string | null;
  category: string;
  unit_price: number;
  total_quantity: number;
  sale_ids: string[];
  statuses: string[];
  primary_status: string;
  order_numbers: string[];
  earliest_sold_at: string;
  latest_sold_at: string;
  records: SalesHistoryRecord[];
}

const STATUS_PRIORITY: Record<string, number> = {
  pending: 0,
  not_available: 1,
  reserved: 2,
  collected: 3,
};

function consolidateSales(records: Array<SalesHistoryRecord & { products?: { category: string | null } | null }>): ConsolidatedSale[] {
  const grouped = new Map<string, ConsolidatedSale>();

  for (const record of records) {
    const key = `${record.product_name}|${record.color_name || ''}|${record.size_name || ''}`;
    const category = record.products?.category || 'Uncategorized';
    const existing = grouped.get(key);

    if (existing) {
      existing.total_quantity += record.quantity_sold;
      existing.sale_ids.push(record.id);
      if (!existing.statuses.includes(record.reservation_status)) {
        existing.statuses.push(record.reservation_status);
      }
      if (record.shopify_order_number && !existing.order_numbers.includes(record.shopify_order_number)) {
        existing.order_numbers.push(record.shopify_order_number);
      }
      if (record.sold_at < existing.earliest_sold_at) existing.earliest_sold_at = record.sold_at;
      if (record.sold_at > existing.latest_sold_at) existing.latest_sold_at = record.sold_at;
      existing.records.push(record);
    } else {
      grouped.set(key, {
        key,
        product_name: record.product_name,
        color_name: record.color_name,
        size_name: record.size_name,
        reference_code: record.reference_code,
        sku: record.sku,
        category,
        unit_price: record.unit_price,
        total_quantity: record.quantity_sold,
        sale_ids: [record.id],
        statuses: [record.reservation_status],
        primary_status: record.reservation_status,
        order_numbers: record.shopify_order_number ? [record.shopify_order_number] : [],
        earliest_sold_at: record.sold_at,
        latest_sold_at: record.sold_at,
        records: [record],
      });
    }
  }

  for (const item of grouped.values()) {
    const sorted = [...item.statuses].sort(
      (a, b) => (STATUS_PRIORITY[a] ?? 99) - (STATUS_PRIORITY[b] ?? 99)
    );
    item.primary_status = sorted[0];
  }

  return Array.from(grouped.values()).sort((a, b) => {
    const catCmp = a.category.localeCompare(b.category);
    if (catCmp !== 0) return catCmp;
    return a.product_name.localeCompare(b.product_name);
  });
}

export function getWeekBounds(date?: Date): { weekStart: string; weekEnd: string } {
  const d = date || new Date();
  const dayOfWeek = d.getUTCDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + mondayOffset);
  monday.setUTCHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return {
    weekStart: monday.toISOString().split('T')[0],
    weekEnd: sunday.toISOString().split('T')[0],
  };
}

export const useSalesHistory = () => {
  const { user } = useAuth();
  const [salesHistory, setSalesHistory] = useState<SalesHistoryRecord[]>([]);
  const [weekStats, setWeekStats] = useState<WeekStats>({ total_sold: 0, pending: 0, reserved: 0, not_available: 0, collected: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [collectionSettings, setCollectionSettings] = useState<CollectionSettings | null>(null);
  const [notificationEmails, setNotificationEmails] = useState<NotificationEmail[]>([]);
  const [consolidatedHistory, setConsolidatedHistory] = useState<ConsolidatedSale[]>([]);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const getLatestWeekWithData = useCallback(async (): Promise<{ weekStart: string; weekEnd: string } | null> => {
    const adminId = getAdminId();
    if (!adminId) return null;

    try {
      const { data, error: fetchError } = await supabase
        .from('shop_sales_history')
        .select('week_start_date, week_end_date')
        .eq('admin_id', adminId)
        .order('week_start_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!data) return null;

      return { weekStart: data.week_start_date, weekEnd: data.week_end_date };
    } catch (err) {
      console.error('Error resolving latest sales week:', err);
      return null;
    }
  }, [getAdminId]);

  const loadSalesHistory = useCallback(async (filters: SalesHistoryFilters = {}) => {
    const adminId = getAdminId();
    if (!adminId) return;

    setLoading(true);
    setError(null);

    try {
      const { weekStart, weekEnd } = filters.weekStart && filters.weekEnd
        ? { weekStart: filters.weekStart, weekEnd: filters.weekEnd }
        : getWeekBounds();

      // Reconcile shop_sales_history from shopify_orders for this week before
      // reading. Sales fulfilled via the cron sync / Worker Terminal are not
      // captured by the webhook-only recordShopSale path; this idempotent,
      // server-side (session-scoped) reconcile makes the view match Shopify.
      // Best-effort: a failure here must never block showing existing rows.
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reconcile-shop-sales`, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ week_start: weekStart, week_end: weekEnd }),
        });
      } catch (reconcileErr) {
        console.error('Shop sales reconcile failed (showing existing rows):', reconcileErr);
      }

      const { data, error: fetchError } = await fetchAllPaginated<
        SalesHistoryRecord & { products?: { category: string | null } | null }
      >((from, to) => {
        let query = supabase
          .from('shop_sales_history')
          .select('*, products(category)')
          .eq('admin_id', adminId)
          .eq('week_start_date', weekStart)
          .eq('week_end_date', weekEnd)
          .order('sold_at', { ascending: false })
          .range(from, to);

        if (filters.status && filters.status !== 'all') {
          query = query.eq('reservation_status', filters.status);
        }

        if (filters.search) {
          const s = filters.search;
          query = query.or(
            `product_name.ilike.%${s}%,sku.ilike.%${s}%,reference_code.ilike.%${s}%,color_name.ilike.%${s}%,size_name.ilike.%${s}%,shopify_order_number.ilike.%${s}%`
          );
        }

        return query;
      });

      if (fetchError) throw fetchError;

      const rawData = (data || []) as Array<SalesHistoryRecord & { products?: { category: string | null } | null }>;
      setSalesHistory(rawData as SalesHistoryRecord[]);
      const consolidated = consolidateSales(rawData);
      setConsolidatedHistory(consolidated);
      setTotalCount(consolidated.length);
    } catch (err) {
      setError('Failed to load sales history');
      console.error('Error loading sales history:', err);
    } finally {
      setLoading(false);
    }
  }, [getAdminId]);

  const loadWeekStats = useCallback(async (weekStart: string, weekEnd: string) => {
    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const { data, error: rpcError } = await supabase.rpc('get_sales_history_week_stats', {
        p_admin_id: adminId,
        p_week_start: weekStart,
        p_week_end: weekEnd,
      });

      if (rpcError) throw rpcError;

      if (data) {
        setWeekStats(data as WeekStats);
      }
    } catch (err) {
      console.error('Error loading week stats:', err);
    }
  }, [getAdminId]);

  const triggerManualReservation = useCallback(async (weekStart?: string, weekEnd?: string): Promise<{ success: boolean; message: string; reservationNumber?: string }> => {
    const adminId = getAdminId();
    if (!adminId) return { success: false, message: 'Not authenticated' };

    try {
      const body: Record<string, unknown> = { manual: true, admin_id: adminId };
      if (weekStart && weekEnd) {
        body.week_start = weekStart;
        body.week_end = weekEnd;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auto-reserve-shop-sales`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();
      return result;
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'Request failed' };
    }
  }, [getAdminId]);

  const markAsCollected = useCallback(async (ids: string[]): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('shop_sales_history')
        .update({ reservation_status: 'collected', updated_at: new Date().toISOString() })
        .in('id', ids);

      if (updateError) throw updateError;
      return true;
    } catch (err) {
      console.error('Error marking as collected:', err);
      return false;
    }
  }, []);

  const loadCollectionSettings = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('shop_collection_settings')
        .select('*')
        .eq('admin_id', adminId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setCollectionSettings(data || {
        admin_id: adminId,
        is_enabled: false,
        collection_days: ['tuesday', 'friday'],
        collection_time: '09:00',
        last_processed_at: null,
      });
    } catch (err) {
      console.error('Error loading collection settings:', err);
    }
  }, [getAdminId]);

  const saveCollectionSettings = useCallback(async (settings: Partial<CollectionSettings>): Promise<boolean> => {
    const adminId = getAdminId();
    if (!adminId) return false;

    try {
      const { error: upsertError } = await supabase
        .from('shop_collection_settings')
        .upsert({
          admin_id: adminId,
          is_enabled: settings.is_enabled ?? false,
          collection_days: settings.collection_days ?? ['tuesday', 'friday'],
          collection_time: settings.collection_time ?? '09:00',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'admin_id' });

      if (upsertError) throw upsertError;

      await loadCollectionSettings();
      return true;
    } catch (err) {
      console.error('Error saving collection settings:', err);
      return false;
    }
  }, [getAdminId, loadCollectionSettings]);

  const loadNotificationEmails = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('shop_notification_emails')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setNotificationEmails(data || []);
    } catch (err) {
      console.error('Error loading notification emails:', err);
    }
  }, [getAdminId]);

  const addNotificationEmail = useCallback(async (email: string, label?: string): Promise<boolean> => {
    const adminId = getAdminId();
    if (!adminId) return false;

    try {
      const { error: insertError } = await supabase
        .from('shop_notification_emails')
        .insert({
          admin_id: adminId,
          email,
          label: label || null,
        });

      if (insertError) throw insertError;

      await loadNotificationEmails();
      return true;
    } catch (err) {
      console.error('Error adding notification email:', err);
      return false;
    }
  }, [getAdminId, loadNotificationEmails]);

  const removeNotificationEmail = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('shop_notification_emails')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await loadNotificationEmails();
      return true;
    } catch (err) {
      console.error('Error removing notification email:', err);
      return false;
    }
  }, [loadNotificationEmails]);

  const toggleNotificationEmail = useCallback(async (id: string, isActive: boolean): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('shop_notification_emails')
        .update({ is_active: isActive })
        .eq('id', id);

      if (updateError) throw updateError;

      await loadNotificationEmails();
      return true;
    } catch (err) {
      console.error('Error toggling notification email:', err);
      return false;
    }
  }, [loadNotificationEmails]);

  const setNotificationEmailFormat = useCallback(async (
    id: string,
    format: 'excel' | 'pdf'
  ): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('shop_notification_emails')
        .update({ report_format: format })
        .eq('id', id);

      if (updateError) throw updateError;

      await loadNotificationEmails();
      return true;
    } catch (err) {
      console.error('Error setting notification email format:', err);
      return false;
    }
  }, [loadNotificationEmails]);

  const sendTestEmail = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    const adminId = getAdminId();
    if (!adminId) return { success: false, message: 'Not authenticated' };

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-brevo-email`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ admin_id: adminId, action: 'send_test' }),
        }
      );

      const result = await response.json();
      return {
        success: result.success ?? false,
        message: result.message || result.error || (response.ok ? 'Email sent' : `Request failed (${response.status})`),
      };
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'Request failed' };
    }
  }, [getAdminId]);

  const downloadPickList = useCallback(async (weekStart: string, weekEnd: string) => {
    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const { data } = await fetchAllPaginated<
        SalesHistoryRecord & { products?: { category: string | null } | null }
      >((from, to) =>
        supabase
          .from('shop_sales_history')
          .select('*, products(category)')
          .eq('admin_id', adminId)
          .eq('week_start_date', weekStart)
          .eq('week_end_date', weekEnd)
          .order('product_name', { ascending: true })
          .range(from, to)
      );

      if (!data || data.length === 0) return;

      const consolidated = consolidateSales(data as Array<SalesHistoryRecord & { products?: { category: string | null } | null }>);
      const rows = ['Category,Item Name,Color,Size,Reference Code,SKU,Unit Price,Total Qty,Status'];
      for (const item of consolidated) {
        rows.push([
          `"${item.category}"`,
          `"${item.product_name}"`,
          `"${item.color_name || ''}"`,
          `"${item.size_name || ''}"`,
          `"${item.reference_code || ''}"`,
          `"${item.sku || ''}"`,
          item.unit_price.toFixed(2),
          item.total_quantity,
          item.primary_status.toUpperCase(),
        ].join(','));
      }

      const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pick-list-${weekStart}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading pick list:', err);
    }
  }, [getAdminId]);

  return {
    salesHistory,
    consolidatedHistory,
    weekStats,
    loading,
    error,
    totalCount,
    collectionSettings,
    notificationEmails,
    loadSalesHistory,
    loadWeekStats,
    getLatestWeekWithData,
    triggerManualReservation,
    markAsCollected,
    loadCollectionSettings,
    saveCollectionSettings,
    loadNotificationEmails,
    addNotificationEmail,
    removeNotificationEmail,
    toggleNotificationEmail,
    setNotificationEmailFormat,
    sendTestEmail,
    downloadPickList,
  };
};
